// The fixed header. No background over the hero at the top of the page; once
// the page has moved past 24px it shrinks and frosts. Below 1024 it is the logo
// and a round button that opens a full-screen night menu.
//
// The menu is a sibling of <header>, not a child, and sits one level under it,
// so the logo and the × stay visible above the sheet. The bar keeps its frost
// while the menu is open, even at the top of the page.
//
// While the menu is open: the page cannot scroll (useBodyScrollLock locks BODY,
// the real scroller), everything behind it is inert, Tab stays inside the bar
// and the sheet, and Escape closes it and returns focus to the button.

import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import CtaLink from "./CtaLink";
import { Menu, Phone, X } from "lucide-react";
import RayesMark from "../../brand/RayesMark";
import useBodyScrollLock from "../../hooks/useBodyScrollLock";
import { telHref } from "./data";
import { DESKTOP, getScrollTop, onScrollFrame } from "./motion/env";
import "./LandingHeader.css";

const COMPACT_AFTER = 24;

export default function LandingHeader({ nav = [], phone = "", ctaText = "", ctaHref = "" }) {
    const [compact, setCompact] = useState(false);
    const [open, setOpen] = useState(false);
    const headerRef = useRef(null);
    const menuRef = useRef(null);
    const toggleRef = useRef(null);

    useBodyScrollLock(open);

    useEffect(() => {
        const read = () => setCompact(getScrollTop() > COMPACT_AFTER);
        read();
        return onScrollFrame(read);
    }, []);

    const close = useCallback(() => {
        setOpen(false);
        toggleRef.current?.focus({ preventScroll: true });
    }, []);

    useEffect(() => {
        if (!open) return undefined;

        const header = headerRef.current;
        const menu = menuRef.current;
        const focusable = () =>
            [...header.querySelectorAll("a, button"), ...menu.querySelectorAll("a, button")]
                .filter((el) => el.getClientRects().length > 0);

        menu.querySelector("a")?.focus({ preventScroll: true });

        const onKey = (e) => {
            if (e.key === "Escape") {
                e.preventDefault();
                close();
                return;
            }
            if (e.key !== "Tab") return;
            const items = focusable();
            if (!items.length) return;
            const first = items[0];
            const last = items[items.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };
        document.addEventListener("keydown", onKey);

        const behind = Array.from(header.closest(".ld-page")?.querySelectorAll(":scope > main, :scope > footer") || []);
        behind.forEach((el) => el.setAttribute("inert", ""));

        return () => {
            document.removeEventListener("keydown", onKey);
            behind.forEach((el) => el.removeAttribute("inert"));
        };
    }, [open, close]);

    // The menu exists only below 1024. Widening the window past that closes it
    // rather than leaving the page locked with its toggle hidden.
    useEffect(() => {
        const wide = window.matchMedia?.(DESKTOP);
        if (!wide) return undefined;
        const onChange = (e) => { if (e.matches) setOpen(false); };
        wide.addEventListener("change", onChange);
        return () => wide.removeEventListener("change", onChange);
    }, []);

    const dismiss = () => setOpen(false);
    const tel = phone ? telHref(phone) : "";

    return (
        <>
            <header
                className={`ld-header ld-night ${compact ? "is-compact" : ""} ${open ? "is-open" : ""}`}
                ref={headerRef}
            >
                <div className="ld-header__bar">
                    <div className="ld-container ld-header__row">
                        <a href="#top" className="ld-logo" aria-label="الريّس — أول الصفحة">
                            <RayesMark size={32} title="" />
                            <span className="ld-logo__name" aria-hidden="true">الريّس</span>
                        </a>

                        <nav className="ld-nav" aria-label="أقسام الصفحة">
                            <ul className="ld-nav__list">
                                {nav.map((l) => (
                                    <li key={l.id}>
                                        <a href={`#${l.id}`} className={`ld-nav__link ${l.pill ? "ld-nav__link--pill" : ""}`}>{l.label}</a>
                                    </li>
                                ))}
                            </ul>
                        </nav>

                        <div className="ld-header__actions">
                            {tel ? (
                                <a href={tel} className="ld-header__phone" aria-label={`اتصل بينا ${phone}`}>
                                    <span className="ld-disc ld-disc--sm"><Phone aria-hidden="true" /></span>
                                </a>
                            ) : null}
                            <Link to="/auth" className="ld-header__login">دخول</Link>
                            <CtaLink href={ctaHref} className="ld-btn ld-btn--primary ld-header__cta">{ctaText}</CtaLink>
                            <button
                                type="button"
                                className="ld-header__menu-btn"
                                ref={toggleRef}
                                aria-expanded={open}
                                aria-controls="ld-menu"
                                aria-label={open ? "اقفل القائمة" : "افتح القائمة"}
                                onClick={() => setOpen((v) => !v)}
                            >
                                <span className="ld-disc ld-disc--md">
                                    {open ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            <div
                id="ld-menu"
                className={`ld-menu ld-night ${open ? "is-open" : ""}`}
                ref={menuRef}
                role="dialog"
                aria-label="القائمة"
                inert={!open}
            >
                <div className="ld-decor" aria-hidden="true">
                    <div className="ld-gridlines" style={{ "--ld-grid-at": "80% 20%" }} />
                    <div className="ld-grain" />
                </div>

                <nav className="ld-menu__body" aria-label="أقسام الصفحة">
                    <ul>
                        {nav.map((l) => (
                            <li key={l.id}>
                                <a href={`#${l.id}`} className="ld-menu__link" onClick={dismiss}>{l.label}</a>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className="ld-glass-night ld-menu__bar">
                    <Link to="/auth" className="ld-menu__login" onClick={dismiss}>دخول</Link>
                    {tel ? (
                        <a href={tel} className="ld-header__phone" aria-label={`اتصل بينا ${phone}`}>
                            <span className="ld-disc ld-disc--sm"><Phone aria-hidden="true" /></span>
                        </a>
                    ) : null}
                    <CtaLink href={ctaHref} className="ld-btn ld-btn--primary ld-menu__cta" onClick={dismiss}>{ctaText}</CtaLink>
                </div>
            </div>
        </>
    );
}
